import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import Skeleton from './common/Skeleton';
import { getCurrency } from '../utils/helpers';
import { useTheme } from '../hooks/useTheme';

const DashboardTab = ({
  selectedStock, stockLoading, stockData, currentPrice, priceChange, priceChangePercent,
  companyName, exchange, logo, industry, isin, signal, refetch,
  chartData, chartDays, setChartDays, candlesLoading
}) => {
  const { chartColors } = useTheme();
  return (
  <>
    {/* Price Hero */}
    <div className="bg-surface backdrop-blur border border-brd-primary rounded-xl p-6">
      {stockLoading ? (
        <div className="space-y-3"><Skeleton className="h-8 w-1/3" /><Skeleton className="h-12 w-1/4" /></div>
      ) : (
        <>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="" className="w-10 h-10 rounded-lg bg-white p-1" />}
              <div>
                <h2 className="text-xl md:text-2xl font-bold mb-1">{companyName}</h2>
                <div className="flex items-center gap-2 text-sm text-txt-secondary flex-wrap">
                  <span className="font-mono">{selectedStock}</span><span>-</span><span>{exchange}</span>
                  {industry && <><span>-</span><span>{industry}</span></>}
                  {isin && <span className="font-mono text-xs text-txt-muted">{isin}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`px-3 py-1.5 rounded-lg text-sm ${signal.bg}`}><span className={`font-bold ${signal.color}`}>{signal.text}</span></div>
              <button onClick={refetch} className="p-2 hover:bg-elevated rounded-lg text-txt-secondary hover:text-txt-primary transition-colors"><RefreshCw size={16} /></button>
            </div>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="text-3xl md:text-4xl font-bold">{getCurrency(selectedStock)}{currentPrice.toFixed(2)}</span>
            <div className={`flex items-center gap-1 text-lg ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {priceChange >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
              <span>{priceChange > 0 ? '+' : ''}{priceChange.toFixed(2)}</span>
              <span>({priceChangePercent > 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%)</span>
            </div>
          </div>
        </>
      )}
    </div>

    {/* Price Chart */}
    <div className="bg-surface backdrop-blur border border-brd-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Andamento Prezzo</h3>
        <div className="flex gap-1">
          {[7, 30, 90, 180].map(d => (
            <button key={d} onClick={() => setChartDays(d)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${chartDays === d ? 'bg-blue-500 text-white' : 'bg-elevated text-txt-secondary hover:text-txt-primary'}`}>{d}G</button>
          ))}
        </div>
      </div>
      {candlesLoading ? <Skeleton className="h-48 sm:h-72 w-full" /> : chartData.length > 0 ? (
        <div className="h-[200px] sm:h-[300px]"><ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs><linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="date" stroke={chartColors.axis} tick={{ fontSize: 11 }} />
            <YAxis stroke={chartColors.axis} tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '8px', fontSize: '13px' }} />
            <Area type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorPrice)" />
          </AreaChart>
        </ResponsiveContainer></div>
      ) : <div className="h-48 sm:h-72 flex items-center justify-center text-txt-muted">Nessun dato disponibile</div>}
    </div>

    {/* Volume Chart */}
    <div className="bg-surface backdrop-blur border border-brd-primary rounded-xl p-6">
      <h3 className="text-lg font-semibold mb-4">Volume Scambi</h3>
      {candlesLoading ? <Skeleton className="h-32 sm:h-48 w-full" /> : chartData.length > 0 ? (
        <div className="h-[140px] sm:h-[200px]"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData.slice(-15)}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="date" stroke={chartColors.axis} tick={{ fontSize: 11 }} />
            <YAxis stroke={chartColors.axis} tick={{ fontSize: 11 }} tickFormatter={v => (v / 1000000).toFixed(0) + 'M'} />
            <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '8px' }} formatter={v => [(v / 1000000).toFixed(2) + 'M', 'Volume']} />
            <Bar dataKey="volume" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer></div>
      ) : null}
    </div>
  </>
  );
};

export default DashboardTab;
